import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '@/components/layout/AdminLayout';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Brain, RefreshCw, AlertTriangle, TrendingUp, TrendingDown, 
  Minus, User, Calendar, Target, Shield, AlertCircle,
  Activity, ChevronRight, Sparkles, Lightbulb
} from 'lucide-react';

const API = '/api';

const PATTERN_CONFIG = {
  stable: { 
    color: 'text-emerald-400', 
    bg: 'bg-emerald-500/10', 
    border: 'border-emerald-500/20',
    icon: Shield,
    label: 'Stable Pattern'
  },
  improving: { 
    color: 'text-blue-400', 
    bg: 'bg-blue-500/10', 
    border: 'border-blue-500/20',
    icon: TrendingUp,
    label: 'Improving Pattern'
  },
  unstable: { 
    color: 'text-red-400', 
    bg: 'bg-red-500/10', 
    border: 'border-red-500/20',
    icon: TrendingDown,
    label: 'Unstable Pattern'
  },
  fluctuating: { 
    color: 'text-amber-400', 
    bg: 'bg-amber-500/10', 
    border: 'border-amber-500/20',
    icon: Activity,
    label: 'Fluctuating Pattern'
  },
};

const SEVERITY_CONFIG = {
  high: { color: 'bg-red-500/20 text-red-400 border-red-500/30' },
  moderate: { color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
  low: { color: 'bg-adm-surface/10 text-white/50 border-white/20' },
};

function DriverCard({ driver }) {
  const severityConfig = SEVERITY_CONFIG[driver.severity] || SEVERITY_CONFIG.moderate;
  
  return (
    <div className="bg-adm-surface/[0.02] border border-white/[0.06] rounded-lg p-4">
      <div className="flex items-start justify-between mb-2">
        <div className="text-xs text-white/40 uppercase tracking-wider">
          {driver.factor.replace(/_/g, ' ')}
        </div>
        <Badge variant="outline" className={`text-[9px] ${severityConfig.color}`}>
          {driver.severity}
        </Badge>
      </div>
      <div className="text-sm text-white/80 leading-relaxed">
        {driver.observation}
      </div>
    </div>
  );
}

function ZoneCard({ zone, type }) {
  const isResponsive = type === 'responsive';
  const bgColor = isResponsive ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-red-500/5 border-red-500/20';
  const iconColor = isResponsive ? 'text-emerald-400' : 'text-red-400';
  const Icon = isResponsive ? Shield : AlertTriangle;
  
  return (
    <div className={`border rounded-lg p-3 ${bgColor}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`w-4 h-4 ${iconColor}`} />
        <span className="text-xs text-white/50 capitalize">{zone.zone.replace(/_/g, ' ')}</span>
      </div>
      <div className="text-sm text-white/70">{zone.observation}</div>
    </div>
  );
}

function FocusRecommendation({ recommendation }) {
  if (!recommendation) return null;
  
  return (
    <Card className="bg-[#C89B7B]/10 border-[#C89B7B]/30">
      <CardContent className="p-5">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-[#C89B7B]/20 flex items-center justify-center flex-shrink-0">
            <Target className="w-6 h-6 text-[#C89B7B]" />
          </div>
          <div className="flex-1">
            <div className="text-[10px] text-[#C89B7B] uppercase tracking-wider mb-1">
              Primary Focus
            </div>
            <div className="text-lg font-medium text-white/90 mb-2">
              {recommendation.action}
            </div>
            <div className="text-sm text-white/50">
              {recommendation.rationale}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function MetricsSummary({ metrics }) {
  if (!metrics) return null;
  
  return (
    <div className="grid grid-cols-5 gap-3 mb-6">
      <div className="bg-adm-surface/[0.02] border border-white/[0.06] rounded-lg p-3 text-center">
        <div className="text-xl font-bold text-white/90">{metrics.adherence_rate}%</div>
        <div className="text-[10px] text-white/40 mt-1">Adherence</div>
      </div>
      <div className="bg-adm-surface/[0.02] border border-white/[0.06] rounded-lg p-3 text-center">
        <div className="text-xl font-bold text-amber-400">{metrics.friction_rate}%</div>
        <div className="text-[10px] text-white/40 mt-1">Friction</div>
      </div>
      <div className="bg-adm-surface/[0.02] border border-white/[0.06] rounded-lg p-3 text-center">
        <div className="text-xl font-bold text-blue-400">{metrics.avg_nss}</div>
        <div className="text-[10px] text-white/40 mt-1">Avg NSS</div>
      </div>
      <div className="bg-adm-surface/[0.02] border border-white/[0.06] rounded-lg p-3 text-center">
        <div className="text-xl font-bold text-white/70">{metrics.nss_variance}</div>
        <div className="text-[10px] text-white/40 mt-1">NSS Variance</div>
      </div>
      <div className="bg-adm-surface/[0.02] border border-white/[0.06] rounded-lg p-3 text-center">
        <div className="text-xl font-bold text-white/70">{metrics.avg_timing_variance_minutes}m</div>
        <div className="text-[10px] text-white/40 mt-1">Avg Delay</div>
      </div>
    </div>
  );
}

export default function PatternNarrative() {
  const [subscribers, setSubscribers] = useState([]);
  const [selectedSubscriber, setSelectedSubscriber] = useState('');
  const [timeWindow, setTimeWindow] = useState('7');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingSubscribers, setLoadingSubscribers] = useState(true);
  
  const token = localStorage.getItem('rf_access_token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  
  // Fetch subscribers
  useEffect(() => {
    setLoadingSubscribers(true);
    fetch(`${API}/admin/correlation-subscribers`, { headers })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d?.subscribers) {
          setSubscribers(d.subscribers);
          const withData = d.subscribers.find(s => s.has_correlation_data);
          if (withData) setSelectedSubscriber(withData.id);
        }
      })
      .finally(() => setLoadingSubscribers(false));
  }, []);
  
  // Fetch pattern narrative
  const fetchNarrative = useCallback(async () => {
    if (!selectedSubscriber) return;
    setLoading(true);
    
    try {
      const res = await fetch(
        `${API}/admin/pattern-narrative/${selectedSubscriber}?days=${timeWindow}`, 
        { headers }
      );
      if (res.ok) {
        setData(await res.json());
      }
    } catch (e) {
      console.error('Error fetching pattern narrative:', e);
    }
    setLoading(false);
  }, [selectedSubscriber, timeWindow]);
  
  useEffect(() => {
    fetchNarrative();
  }, [fetchNarrative]);
  
  const patternConfig = data?.overall_pattern ? PATTERN_CONFIG[data.overall_pattern] : null;
  const PatternIcon = patternConfig?.icon || Activity;
  
  return (
    <AdminLayout>
      <div className="p-6 max-w-[1000px] mx-auto" data-testid="pattern-narrative">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-semibold text-white/90 flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-[#C89B7B]" />
              Pattern Narrative
            </h1>
            <p className="text-xs text-white/40 mt-1">
              Biological pattern interpretation for counselor/admin preparation
            </p>
            <div className="flex items-center gap-3 mt-2 text-[8px] text-white/15">
              <span>Role: Admin / Counselor</span>
              <span>Data: execution_logs + MindFlo + schedule_items + correlations</span>
              <span>Purpose: Generate counselor prep insights and biological narrative summaries</span>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Select value={selectedSubscriber} onValueChange={setSelectedSubscriber} disabled={loadingSubscribers}>
              <SelectTrigger className="w-[220px] bg-adm-surface/5 border-white/10 text-white h-9 text-xs" data-testid="subscriber-select">
                <User className="w-3 h-3 mr-2 text-white/40" />
                <SelectValue placeholder={loadingSubscribers ? "Loading..." : "Select subscriber"} />
              </SelectTrigger>
              <SelectContent className="bg-[#0a0a0f] border-white/10">
                {subscribers.map(s => (
                  <SelectItem key={s.id} value={s.id} className="text-white text-xs">
                    <div className="flex items-center gap-2">
                      <span>{s.full_name}</span>
                      {s.has_correlation_data && (
                        <span className="text-[9px] bg-emerald-500/20 text-emerald-400 px-1.5 py-0.5 rounded">Ready</span>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={timeWindow} onValueChange={setTimeWindow}>
              <SelectTrigger className="w-[130px] bg-adm-surface/5 border-white/10 text-white h-9 text-xs" data-testid="time-window-select">
                <Calendar className="w-3 h-3 mr-2 text-white/40" />
                <SelectValue placeholder="Time window" />
              </SelectTrigger>
              <SelectContent className="bg-[#0a0a0f] border-white/10">
                <SelectItem value="7" className="text-white text-xs">Last 7 days</SelectItem>
                <SelectItem value="14" className="text-white text-xs">Last 14 days</SelectItem>
              </SelectContent>
            </Select>
            
            <Button onClick={fetchNarrative} size="sm" variant="ghost" className="text-xs text-white/50 h-9" data-testid="refresh-btn">
              <RefreshCw className="w-3 h-3 mr-1" /> Refresh
            </Button>
          </div>
        </div>
        
        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center h-64 text-white/40">
            <RefreshCw className="w-5 h-5 animate-spin mr-2" />
            Analyzing patterns...
          </div>
        ) : !selectedSubscriber ? (
          <div className="text-center py-12 text-white/40">
            <User className="w-12 h-12 mx-auto mb-4 opacity-20" />
            <p>Select a subscriber to view pattern narrative</p>
          </div>
        ) : !data?.has_data ? (
          <div className="text-center py-12">
            <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-amber-400/40" />
            <p className="text-white/60 mb-2">{data?.message || "Insufficient data for analysis"}</p>
            {data?.data_summary && (
              <div className="text-xs text-white/40 mt-4">
                Current: {data.data_summary.execution_days} execution days, {data.data_summary.mindflo_days} MindFlō days
                <br />
                Required: {data.data_summary.minimum_required?.execution_days} execution days, {data.data_summary.minimum_required?.mindflo_days} MindFlō days
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {/* Subscriber Info */}
            <Card className="bg-adm-surface/[0.02] border-white/[0.06]">
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-[#C89B7B]/20 flex items-center justify-center">
                    <User className="w-5 h-5 text-[#C89B7B]" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-white/90">{data.subscriber?.full_name}</div>
                    <div className="text-xs text-white/40">{data.phase?.name}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Badge className="text-xs bg-adm-surface/5 text-white/40 border-0">
                    {data.analysis_period?.execution_days} exec days • {data.analysis_period?.mindflo_days} MindFlō days
                  </Badge>
                  {data.pattern_direction && (
                    <Badge className={`text-[10px] ${
                      data.pattern_direction === 'improving' ? 'bg-emerald-500/20 text-emerald-400' :
                      data.pattern_direction === 'declining' ? 'bg-red-500/20 text-red-400' :
                      data.pattern_direction === 'stable' ? 'bg-blue-500/20 text-blue-400' :
                      'bg-amber-500/20 text-amber-400'
                    } border-0`}>
                      {data.pattern_direction === 'improving' && <TrendingUp className="w-3 h-3 mr-1" />}
                      {data.pattern_direction === 'declining' && <TrendingDown className="w-3 h-3 mr-1" />}
                      {data.pattern_direction === 'stable' && <Minus className="w-3 h-3 mr-1" />}
                      {data.pattern_direction === 'fluctuating' && <Activity className="w-3 h-3 mr-1" />}
                      {data.pattern_direction.charAt(0).toUpperCase() + data.pattern_direction.slice(1)}
                    </Badge>
                  )}
                  {patternConfig && (
                    <Badge className={`${patternConfig.bg} ${patternConfig.color} ${patternConfig.border} border flex items-center gap-1`}>
                      <PatternIcon className="w-3 h-3" />
                      {patternConfig.label}
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
            
            {/* Metrics Summary */}
            <MetricsSummary metrics={data.metrics_summary} />
            
            {/* Main Narrative */}
            <Card className="bg-adm-surface/[0.02] border-white/[0.06]">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-white/70 flex items-center gap-2">
                  <Brain className="w-4 h-4 text-[#C89B7B]" />
                  Pattern Interpretation
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-base text-white/80 leading-relaxed" data-testid="narrative-text">
                  {data.narrative}
                </p>
              </CardContent>
            </Card>
            
            {/* Focus Recommendation */}
            <FocusRecommendation recommendation={data.focus_recommendation} />
            
            {/* Key Drivers */}
            {data.key_drivers?.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-white/70 mb-3 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-400" />
                  Key Drivers
                </h3>
                <div className="space-y-3">
                  {data.key_drivers.map((driver, i) => (
                    <DriverCard key={i} driver={driver} />
                  ))}
                </div>
              </div>
            )}
            
            {/* Zones Grid */}
            <div className="grid grid-cols-2 gap-4">
              {/* Responsive Zones */}
              {data.responsive_zones?.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-white/70 mb-3 flex items-center gap-2">
                    <Shield className="w-4 h-4 text-emerald-400" />
                    Responsive Zones
                  </h3>
                  <div className="space-y-2">
                    {data.responsive_zones.map((zone, i) => (
                      <ZoneCard key={i} zone={zone} type="responsive" />
                    ))}
                  </div>
                </div>
              )}
              
              {/* Risk Zones */}
              {data.risk_zones?.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-white/70 mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-400" />
                    Risk Zones
                  </h3>
                  <div className="space-y-2">
                    {data.risk_zones.map((zone, i) => (
                      <ZoneCard key={i} zone={zone} type="risk" />
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            {/* Methodology Note */}
            <div className="p-4 bg-adm-surface/[0.01] rounded-lg border border-white/5">
              <div className="flex items-start gap-3">
                <Lightbulb className="w-4 h-4 text-white/30 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-white/40 leading-relaxed">
                  This interpretation reflects patterns observed in execution and neuro-state data over the selected period.
                  Use alongside clinical assessment for comprehensive care planning.
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
